import { Fragment, useEffect } from 'react'
import { withHandlers } from 'recompose'

import { cancelWaitingList } from 'grs/actions'
import { getReservations } from '../actions'

import { cancelWaitingListRecordPendingSelector } from 'dashboard-transactions/selectors'
import { recentReservationsSelector, reservationPendingSelector } from '../selectors'

import { getDateTimeFormat } from 'i18n/utils'
import SpecialOffers from '@components/SpecialOffers'
import Button from '@components/Button'
import CircleLoader from '@components/CircleLoader'

import useRequestPending from 'shared/hooks/useRequestPending'

import './ReservationsList.scss'

const Reservation = ({
    location,
    confirmationNumber,
    currencySymbol,
    amount,
    cardLast4,
    fromDate,
    toDate,
    vehicles,
    isMonthlyPass,
}) => {
    const typeLabel = <T>{isMonthlyPass ? 'dashboard.label.subscription' : 'dashboard.label.booking'}</T>

    const formattedFromDate = fromDate.format(getDateTimeFormat())
    const formattedToDate = toDate.format(getDateTimeFormat())

    return (
        <div className="reservation-container">
            <div className="reservation">
                <div className="summary">
                    <h4 className="text-extra-large">{typeLabel}</h4>
                    <hr />
                    <p className="detail location">
                        <span>{location}</span>
                    </p>
                    <p className="detail time">
                        <span>
                            <span>
                                <T>label.parking.start</T>: {formattedFromDate}
                            </span>
                            <span>
                                <T>label.parking.end</T>: {formattedToDate}
                            </span>
                        </span>
                    </p>
                    {vehicles.length > 0 && (
                        <p className="detail vehicle">{vehicles.map(vehicle => vehicle.tag).join(', ')}</p>
                    )}
                </div>

                <div className="details">
                    <h4 className="text-extra-large">{typeLabel}</h4>
                    <hr />

                    <h6>
                        <T>dashboard.label.transaction</T>
                    </h6>
                    <p className="detail number">{confirmationNumber}</p>

                    <h6>
                        <T>dashboard.label.type</T>
                    </h6>
                    <p className="detail type">{typeLabel}</p>

                    <h6>
                        <T>dashboard.label.location</T>
                    </h6>
                    <p className="detail location">{location}</p>

                    <h6>Time</h6>
                    <p className="detail time">
                        <span>
                            <T>label.parking.start</T>: {formattedFromDate}
                        </span>
                        <span>
                            <T>label.parking.end</T>: {formattedToDate}
                        </span>
                    </p>

                    {cardLast4 && (
                        <Fragment>
                            <h6>
                                <T>dashboard.label.card</T>
                            </h6>
                            <p className="detail credit-card">{cardLast4}</p>
                        </Fragment>
                    )}

                    <h6>
                        <T>dashboard.label.price</T>
                    </h6>
                    <p className="detail price">{`${currencySymbol}${amount}`}</p>
                </div>
            </div>
        </div>
    )
}

const enhanceWaitingListRecord = compose(
    connect(
        createStructuredSelector({
            pending: cancelWaitingListRecordPendingSelector,
        }),
        { cancelWaitingList }
    ),
    withHandlers({
        onClickCancel:
            ({ waitingListKey, cancelWaitingList }) =>
            () =>
                cancelWaitingList(waitingListKey),
    })
)

const $WaitingListRecord = ({
    location,
    expiryDate,
    isConfirmed,
    locationId,
    rateId,
    waitingListKey,
    onClickCancel,
    pending,
}) => (
    <div className="reservation-container">
        <div className="waiting-list">
            <div className="summary">
                <div>
                    {expiryDate && (
                        <div className="expires">
                            <T>dashboard.label.expires</T> {expiryDate.format(getDateTimeFormat())}
                        </div>
                    )}

                    <h4>
                        <T>dashboard.label.waitingList</T>
                    </h4>
                    <hr />
                    <p className="detail location">
                        <span>{location}</span>
                    </p>
                </div>
                <div className="buttons">
                    <Button onClick={onClickCancel} btnStyle="outline" isLoading={pending}>
                        <T>dashboard.label.cancelWaitingList</T>
                    </Button>
                    {isConfirmed && (
                        <Link
                            className="btn btn-primary"
                            to={{
                                pathname: `/booking/${locationId}/subscriptions`,
                                search: { rateId, waitingListKey },
                            }}>
                            <T>dashboard.label.bookWaitingList</T>
                        </Link>
                    )}
                </div>
            </div>
        </div>
    </div>
)

const WaitingListRecord = enhanceWaitingListRecord($WaitingListRecord)

const List = ({ reservations }) => (
    <ul className="reservations-list">
        {reservations.slice(0, 3).map(reservation => (
            <li key={reservation.reservationId}>
                {React.createElement(reservation.waitingListKey ? WaitingListRecord : Reservation, reservation)}
            </li>
        ))}
    </ul>
)

const ReservationsList = ({ reservations, reservationPending, getReservations }) => {
    const { isLoading } = useRequestPending(reservationPending)

    useEffect(() => {
        getReservations()
    }, [])

    return (
        <div>
            <div className="reservations-list">
                <h3 className="font-weight-bold text-center">
                    <T>dashboard.label.curr.transactions</T>
                </h3>
                {isLoading ? (
                    <div className="d-flex justify-content-center mt-3">
                        <CircleLoader />
                    </div>
                ) : (
                    <List reservations={reservations} />
                )}
            </div>
            <SpecialOffers />
        </div>
    )
}

const mapStateToProps = createStructuredSelector({
    reservations: recentReservationsSelector,
    reservationPending: reservationPendingSelector,
})

const mapDispatchToProps = {
    getReservations,
}

export default connect(mapStateToProps, mapDispatchToProps)(ReservationsList)
